import { Button, Input } from 'components/atoms';
import { Form, Formik, FormikHelpers } from 'formik';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

import { FormInput } from 'components/molecules';
import { PublicPage } from 'components/templates';
import { useMutateOptions } from 'hooks';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { PiCaretLeft, PiUser } from 'react-icons/pi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRegister } from 'services/auth/register';
import * as Yup from 'yup';

type RegisterFormModel = {
  userName: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  password: string;
  repeatPassword: string;
};

const initialValues: RegisterFormModel = {
  userName: '',
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  password: '',
  repeatPassword: '',
};

const validationSchema = Yup.object().shape({
  userName: Yup.string()
    .min(3, i18next.t('formik-error.too-short'))
    .max(20, i18next.t('formik-error.too-long'))
    .required(i18next.t('formik-error.required')),

  firstName: Yup.string()
    .min(1, i18next.t('formik-error.too-short'))
    .max(50, i18next.t('formik-error.too-long'))
    .required(i18next.t('formik-error.required')),

  lastName: Yup.string()
    .min(1, i18next.t('formik-error.too-short'))
    .max(50, i18next.t('formik-error.too-long'))
    .required(i18next.t('formik-error.required')),

  email: Yup.string()
    .email(i18next.t('formik-error.invalid-email'))
    .required(i18next.t('formik-error.required')),

  phoneNumber: Yup.string()
    .matches(/^[0-9]{9,15}$/, i18next.t('formik-error.invalid-phone'))
    .required(i18next.t('formik-error.required')),

  password: Yup.string()
    .min(6, i18next.t('formik-error.invalid-password'))
    .max(50, i18next.t('formik-error.too-long'))
    .required(i18next.t('formik-error.required')),

  repeatPassword: Yup.string()
    .oneOf(
      [Yup.ref('password')],
      i18next.t('formik-error.passwords-must-match')
    )
    .required(i18next.t('formik-error.required')),
});

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const register = useRegister();
  const mutateOptions = useMutateOptions<RegisterFormModel>();



  const onRegister = async (
    values: RegisterFormModel,
    helpers: FormikHelpers<RegisterFormModel>
  ) => {
    const payload: RegisterFormModel = {
      ...values,
      phoneNumber: String(values.phoneNumber ?? ''),
    };
    await register.mutateAsync(payload, {
      ...mutateOptions(helpers),
      onSuccess: () =>
        navigate(`/register/confirm?email=${values.email}`),
    });
  };

  return (
    <Formik
      validateOnBlur
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={onRegister}>
      {({ values }) => (
        <PublicPage
          header={
            <>
              <PiUser />
              {t('register.header')}
            </>
          }
          footer={
            <div
              className='flex gap-2 cursor-pointer p-4 items-center'
              onClick={() => navigate('/login')}>
              <PiCaretLeft />
              {t('reset-password-request.back')}
            </div>
          }>
          <Form style={{ gap: 10 }}>
            <FormInput.Text
              name='userName'
              type='string'
              label={t('input.label.userName')}
              borderless
              appBackground
            />
            <Input.ComplexWrapper sizes={[50, 50]} className='max-w-[350px]'>
              <FormInput.Text
                name='firstName'
                type='string'
                label={t('input.label.firstName')}
                borderless
                appBackground
              />
              <FormInput.Text
                name='lastName'
                type='string'
                label={t('input.label.lastName')}
                borderless
                appBackground
              />
            </Input.ComplexWrapper>
            <div />
            <FormInput.Text
              name='email'
              type='email'
              label={t('input.label.email')}
              borderless
              appBackground
            />
            <FormInput.Text
              name='phoneNumber'
              type='number'
              label={t('input.label.phoneNumber')}
              borderless
              appBackground
            />
            <div />
            <FormInput.Text
              name='password'
              type='password'
              label={t('input.label.password')}
              borderless
              appBackground
            />
            <FormInput.Text
              name='repeatPassword'
              type='password'
              label={t('input.label.repeatPassword')}
              borderless
              appBackground
            />
            <div />
            <div />
            <Button
              type='submit'
              isLoading={register.isPending}
              variant={BUTTON_VARIANT.submit}
              fullWidth>
              <Button.Text text={t('register.button')} />
            </Button>
          </Form>
        </PublicPage>
      )}
    </Formik>
  );
};
export default Register;
